import { env } from '@/utils/env'

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent'

const logRanks: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 50,
}

// Default dev level is `info` so the console isn't flooded by per-request
// lifecycle traces and other diagnostics. Set VITE_LOG_LEVEL=debug in
// .env.local when you actually want them.
const configuredLevel: LogLevel = env.logLevel ?? (env.isProduction ? 'warn' : 'info')

let currentRequestId: string | undefined

export const setLoggerRequestId = (requestId?: string) => {
  currentRequestId = requestId
}

const shouldLog = (level: LogLevel) => logRanks[level] >= logRanks[configuredLevel]

const formatPrefix = (scope?: string) => {
  const requestPart = currentRequestId ? `req:${currentRequestId}` : undefined
  const scopePart = scope ? `scope:${scope}` : undefined
  const parts = [requestPart, scopePart].filter(Boolean)
  return parts.length ? `[${parts.join(' ')}]` : undefined
}

export const createLogger = (scope?: string) => {
  const buildArgs = (args: unknown[]) => {
    const prefix = formatPrefix(scope)
    if (!prefix) return args
    return [prefix, ...args]
  }

  return {
    debug: (...args: unknown[]) => {
      if (!shouldLog('debug')) return
      console.debug(...buildArgs(args))
    },
    info: (...args: unknown[]) => {
      if (!shouldLog('info')) return
      console.info(...buildArgs(args))
    },
    warn: (...args: unknown[]) => {
      if (!shouldLog('warn')) return
      console.warn(...buildArgs(args))
    },
    error: (...args: unknown[]) => {
      if (!shouldLog('error')) return
      console.error(...buildArgs(args))
    },
    group: (...args: unknown[]) => {
      if (!shouldLog('debug')) return
      console.group(...buildArgs(args))
    },
    groupEnd: () => {
      if (!shouldLog('debug')) return
      console.groupEnd()
    },
    table: (tabularData?: unknown, properties?: readonly string[]) => {
      if (!shouldLog('debug')) return
      console.table(tabularData, properties)
    },
  }
}
